import {
    SidebarBoxKey,
    type MobileSidebarBox as MobileSidebarBoxType,
    type StudentDashboardConfig,
} from 'AdmissionsGuidance';
import { useTargetBrandConfig } from 'FrontRoyalAngular';
import { memo, useEffect, useState } from 'react';
import { MobileSidebarBox } from './MobileSidebarBox';
import { OfflineBox } from '../boxes/OfflineBox';
import { isCombinedMobileSidebarBox } from '../StudentDashboardSidebarTypeGuards';
import { MobileBoxContextProvider } from './MobileBoxContext';

function MobileSidebarComponent({ studentDashboardConfig }: { studentDashboardConfig: StudentDashboardConfig }) {
    const [boxes, setBoxes] = useState<MobileSidebarBoxType[]>([]);
    const brandConfig = useTargetBrandConfig();
    const colorClassnames = brandConfig.mobileSidebarBoxColorClasses;
    const offlineBoxIndex = boxes.findIndex(box => box.key === SidebarBoxKey.offline);

    useEffect(() => {
        studentDashboardConfig.mobileSidebarBoxes().then(_boxes => {
            const visibleBoxes = _boxes.filter(box =>
                isCombinedMobileSidebarBox(box)
                    ? !box.primary.shouldHide && !!box.primary.mobileContent
                    : !box.shouldHide && !!box.mobileContent,
            );
            setBoxes(visibleBoxes);
        });
    }, [studentDashboardConfig, offlineBoxIndex]);

    return (
        <div className="flex flex-col gap-0 md:hidden">
            {boxes.map((boxConfig, index) => {
                // Skip the offline box when calculating the index that determines the box color
                const adjustedIndex = offlineBoxIndex >= 0 && index > offlineBoxIndex ? index - 1 : index;
                return boxConfig.key === SidebarBoxKey.offline ? (
                    // Special case for offline box, which has its own style, is not clickable, and is not colored with
                    // the colorClassnames array
                    <OfflineBox key={boxConfig.key} mobile />
                ) : (
                    // Provide the boxConfig to the context so that the box component can access it when rendering
                    // multiple boxes in the expanded drawer content and also adjust box styles for the drawer view
                    <MobileBoxContextProvider
                        key={isCombinedMobileSidebarBox(boxConfig) ? boxConfig.primary.key : boxConfig.key}
                        boxConfig={boxConfig}
                    >
                        <MobileSidebarBox
                            colorClassname={colorClassnames[adjustedIndex % colorClassnames.length]}
                            boxConfig={boxConfig}
                        />
                    </MobileBoxContextProvider>
                );
            })}
        </div>
    );
}

export const MobileSidebar = memo(MobileSidebarComponent);
